import { ActionList, ActionMenu, Box, Text } from "@primer/react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { appConst } from "../AppConst";
import { useAppDispatch } from "../hooks/useAppDispatch";
import { useAppSelector } from "../hooks/useAppSelector";
import { useAuth } from "../hooks/useAuth";
import { useIsMobile } from "../hooks/useIsMobile";
import { IUserInfo } from "../model/respone/auth/ILoginRespone";
import { rootActions } from "../state/actions/rootActions";
import { eReducerStatusBase } from "../state/reducer-model/eReducerStatusBase";
import { useActivePeriodId } from "../hooks/useActivePeriodId";
import { getActivePeriodId, setActivePeriodId, markPeriodResolved, getAccessToken } from "../api/apiClient";
import { registrationApi } from "../api/registration/registrationApi";
import { colors } from "../theme/tokens";
import { isDevSession } from "../helpers/jwt";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { DevResetPasswordDialog } from "./DevResetPasswordDialog";
import { SessionTimer } from "./SessionTimer";

interface INavItem {
    to?: string;
    href?: string;
    label: string;
    requireAccess?: boolean;
    external?: boolean;
}

/** Format ISO/datetime string → "DD/MM HH:mm". Trả "—" nếu invalid. */
const formatTime = (iso?: string): string => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso; // fallback nếu server trả raw string không parse được
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const HH = String(d.getHours()).padStart(2, "0");
    const MI = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm} ${HH}:${MI}`;
};

/** 1 ô thống kê trong dải thông tin mobile — nhãn nhỏ trên, giá trị đậm dưới. */
const StatCell = ({ label, value, accent }: { label: string; value: ReactNode; accent?: boolean }) => (
    <Box
        sx={{
            flex: 1,
            minWidth: 0,
            textAlign: "center",
            px: 1,
            py: "5px",
            borderRight: "1px solid #e3e6ea",
            ":last-child": { borderRight: 0 },
        }}
    >
        <Text sx={{ display: "block", fontSize: 0, color: "#8a9099", lineHeight: 1.4 }}>{label}</Text>
        <Text
            sx={{
                display: "block",
                fontSize: 1,
                fontWeight: 700,
                lineHeight: 1.3,
                whiteSpace: "nowrap",
                color: accent ? colors.primary : "#333",
                fontVariantNumeric: "tabular-nums",
            }}
        >
            {value}
        </Text>
    </Box>
);

/**
 * Format số tiền compact cho ô thống kê hẹp: 1_500_000 → "1.5tr", 12_000 → "12k", âm vẫn giữ dấu.
 * Mobile bar chật → tránh full "1.500.000đ" tràn dòng.
 */
const formatMoneyCompact = (value: number): string => {
    const abs = Math.abs(value);
    const sign = value < 0 ? "-" : "";
    if (abs >= 1_000_000) {
        const m = abs / 1_000_000;
        return `${sign}${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}tr`;
    }
    if (abs >= 1_000) {
        const k = abs / 1_000;
        return `${sign}${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
    }
    return `${sign}${abs}`;
};

/**
 * Dải thông tin dưới header — CHỈ hiển thị ở bản mobile (header xs vốn trống).
 * Mặc định 3 ô: TC đã ĐK, hạn TC cho phép, Phiên đăng nhập.
 * Đợt bật `tinh_hoc_phi_theo_so_du` → chèn thêm 2 ô Số dư + Còn lại (tổng 5 ô).
 * Đợt tắt cờ → KHÔNG render 2 ô tiền (giữ ô rộng hơn, tránh hiển thị "—" gây nhiễu).
 */
const MobileInfoBar = ({ user, tongTC }: { user: IUserInfo; tongTC: number }) => {
    const min = user.so_tc_min_duoc_dk ?? 0;
    const max = user.so_tc_max_duoc_dk ?? 0;
    const financial = useAppSelector(s => s.financial.data);
    const showMoney = !!financial?.tinh_hoc_phi_theo_so_du;
    const conLai = financial ? financial.so_du - (financial.da_dk_chua_nop ?? 0) - financial.tong_tien_phai_nop : 0;
    const vuotSoDu = showMoney && conLai < 0;

    return (
        <Box sx={{ borderTop: "1px solid #eef0f2", px: "12px", py: "8px", bg: "#fff" }}>
            {/* Ô thống kê */}
            <Box
                sx={{
                    display: "flex",
                    border: "1px solid #e3e6ea",
                    borderRadius: "8px",
                    bg: "#fafbfc",
                }}
            >
                <StatCell label="Đã ĐK" value={`${tongTC} TC`} accent />
                <StatCell label="Cho phép" value={`${min}–${max} TC`} />
                {showMoney && (
                    <>
                        <StatCell
                            label="Số dư"
                            value={formatMoneyCompact(financial?.so_du ?? 0)}
                        />
                        <StatCell
                            label="Còn lại"
                            value={
                                <span style={{ color: vuotSoDu ? colors.danger : undefined }}>
                                    {formatMoneyCompact(conLai)}
                                </span>
                            }
                        />
                    </>
                )}
                <StatCell label="Phiên" value={<SessionTimer inline />} />
            </Box>
        </Box>
    );
};

/**
 * Clone Old `_Layout.cshtml` header structure:
 *   [Logo + webName + tagline 2 dòng] - [Menu horizontal] - [User info + 3-dot dropdown]
 * Box-shadow under header, padding 10px 15px.
 */
const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile();
    const { user } = useAuth();
    const { hasAccess, inQueue, position, openAt, closeAt } = useAppSelector(s => s.queue);
    const myList = useAppSelector(s => s.registration.myList);
    const myStatus = useAppSelector(s => s.registration.myStatus);
    const financial = useAppSelector(s => s.financial.data);
    const financialStatus = useAppSelector(s => s.financial.status);
    const { logoUrl, webName, instructionsLink } = appConst.branding;
    const [isChangePassOpen, setIsChangePassOpen] = useState(false);
    const [isDevResetOpen, setIsDevResetOpen] = useState(false);
    /** Phiên đăng nhập bằng mật khẩu dev → hiện thêm menu "Reset mật khẩu SV (hỗ trợ)".
     *  Token cố định trong 1 phiên nên chỉ tính 1 lần. */
    const devSession = useMemo(() => isDevSession(), []);

    /** Multi-period: đợt đang chọn (reactive). Header là component luôn mounted khi đã login →
     *  đứng ra BOOTSTRAP resolve đợt (nếu chưa có) để mọi request gắn X-Period-Id, tránh race
     *  login lần đầu (Header/Landing gọi /my,/financial,/queue trước khi đợt được set → 400). */
    const activePeriodId = useActivePeriodId();
    useEffect(() => {
        if (!user) return;
        if (getActivePeriodId()) return; // Hub adopt / localStorage đã có → khỏi fetch.
        let cancelled = false;
        registrationApi.GetPeriods().then(res => {
            if (cancelled) return;
            const data = res.is_success && res.data ? res.data : [];
            if (data.length > 0) {
                const ids = data.map(p => p.periodId);
                let pid = getActivePeriodId() ?? 0;
                if (!ids.includes(pid)) pid = ids[0] ?? 0;
                if (pid > 0) setActivePeriodId(pid); // notify → các gate dưới chạy.
            }
            // Đánh dấu đã resolve xong (kể cả rỗng) → Landing/Classes thoát spinner, hiện "chưa mở cổng".
            markPeriodResolved();
        });
        return () => { cancelled = true; };
    }, [user]);

    /** Auto-load myList khi user authenticated → header hiện "Đã ĐK: X" ngay sau login.
     *  Gate theo activePeriodId>0: /my cần X-Period-Id, gọi sớm sẽ 400 PERIOD_REQUIRED. */
    useEffect(() => {
        if (user && activePeriodId > 0 && myStatus === eReducerStatusBase.is_not_initialization) {
            dispatch(rootActions.registration.LOAD_MY_START());
        }
    }, [user, activePeriodId, myStatus, dispatch]);

    /** Auto-start poll số dư (60s) khi đã login. Gate theo activePeriodId>0 (cần X-Period-Id). */
    useEffect(() => {
        if (user && activePeriodId > 0 && financialStatus === eReducerStatusBase.is_not_initialization) {
            dispatch(rootActions.financial.LOAD_START());
        }
    }, [user, activePeriodId, financialStatus, dispatch]);

    /** Tổng tín chỉ đã đăng ký (chỉ row trang_thai = 1 = Confirmed). */
    const tongTC = useMemo(
        () => myList.filter(r => r.trang_thai === 1).reduce((sum, r) => sum + (r.so_tin_chi || 0), 0),
        [myList]
    );

    /** Có data TC để hiển thị (avoid show "0 - 0" khi chưa load). */
    const hasTcInfo = !!user && (user.so_tc_min_duoc_dk || user.so_tc_max_duoc_dk);
    /** Đợt bật học phí theo số dư? → hiển thị "Số dư/Còn lại" trên header (cả desktop + mobile).
     *  Cờ off → ẩn hoàn toàn, KHÔNG hiện placeholder. */
    const showMoneyOnHeader = !!financial?.tinh_hoc_phi_theo_so_du;
    const conLaiOnHeader = financial ? financial.so_du - (financial.da_dk_chua_nop ?? 0) - financial.tong_tien_phai_nop : 0;
    const vuotSoDuOnHeader = showMoneyOnHeader && conLaiOnHeader < 0;
    /** Ưu tiên `queue.openAt`/`closeAt` từ HASH `reg:period:{periodId}:meta` — ESS ghi đè ngay khi admin
     *  sửa đợt nên SV F5 thấy giờ mới luôn, không cần logout-login. Fallback JWT claim
     *  (`tu_ngay_dkhocdi` / `thoi_gian_bat_dau`) khi queue chưa load (init / SignalR offline). */
    const startTime = openAt ?? user?.tu_ngay_dkhocdi ?? user?.thoi_gian_bat_dau ?? user?.thoi_gian_bat_dau_toan_truong;
    const endTime = closeAt ?? user?.den_ngay_dkhocdi ?? user?.thoi_gian_ket_thuc ?? user?.thoi_gian_ket_thuc_toan_truong;
    const hasTimeInfo = !!user && !!startTime;

    // Old nav: Trang chủ / Xem lịch đăng ký / Đăng ký tín chỉ / Hướng dẫn.
    // Cả "Xem lịch" + "Đăng ký" đều map tới /classes (3-section combined giống Old).
    const navItems: INavItem[] = [
        { to: "/", label: "Trang chủ" },
        // { to: "/classes", label: "Xem lịch đăng ký", requireAccess: true },
        { to: "/classes", label: "Đăng ký tín chỉ", requireAccess: true },
        ...(instructionsLink
            ? [{ href: instructionsLink, label: "Hướng dẫn", external: true }]
            : []),
    ];

    const handleLogout = () => {
        const unit = appConst.sso.unit;

        // SSO: KHÔNG dispatch LOGOUT tại đây. Header nằm trong ProtectedRoute — clear token làm
        // ProtectedRoute lập tức render <Navigate to="/login">, cướp mất điều hướng sang /signout-oidc
        // (không kịp gọi end-session IdP). Đẩy sang /signout-oidc (route public) để TRANG ĐÓ tự clear
        // token + redirect IdP — lúc này đã thoát ProtectedRoute nên dispatch LOGOUT an toàn.
        if (unit === "namviet" || (unit === "vnpt" && appConst.sso.logout)) {
            navigate("/signout-oidc", { replace: true });
            return;
        }

        // Không bật SSO → logout local thường.
        dispatch(rootActions.auth.LOGOUT());
        navigate("/login", { replace: true });
    };

    const handleChangePassword = () => {
        // Khi cấu hình URL_PAGE_MAIN (portal chính) → đổi mật khẩu tập trung ở portal đó:
        // redirect sang {URL_PAGE_MAIN}/ResetPassword.html?token=<JWT> (giống MARKMAN). Hợp với
        // deployment SSO — mật khẩu do cổng tập trung quản lý, không đổi nội bộ. Rỗng → dialog nội bộ.
        if (appConst.urlPageMain) {
            const token = getAccessToken() ?? "";
            window.location.href = `${appConst.urlPageMain}/ResetPassword.html?token=${encodeURIComponent(token)}`;
            return;
        }
        setIsChangePassOpen(true);
    };

    return (
        <Box
            as="header"
            sx={{
                bg: "#fff",
                boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                position: "sticky",
                top: 0,
                // KHÔNG set zIndex — nếu có z-index, sticky tạo stacking context
                // → Primer ActionMenu portal (rendered ở body root) sẽ bị header che.
            }}
        >
          <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: "10px 15px",
            }}
          >
            {/* Logo + branding */}
            <Box
                as="a"
                href="/"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    textDecoration: "none",
                    color: "inherit",
                    flexShrink: 0,
                }}
            >
                {logoUrl && (
                    <Box
                        as="img"
                        src={logoUrl}
                        alt={webName}
                        sx={{ maxHeight: 60, objectFit: "contain", flexShrink: 0 }}
                    />
                )}
                <Box
                    sx={{
                        display: ["none", "flex"],
                        flexDirection: "column",
                        lineHeight: 1.2,
                        textTransform: "uppercase",
                    }}
                >
                    <Text sx={{ fontWeight: 700, fontSize: [4, 4, 4, 5], color: "#333" }}>
                        {webName}
                    </Text>
                    {/* Dòng phụ chỉ hiện ở lg+ — tránh chiếm ngang gây tràn header trên iPad (md) */}
                    <Text sx={{ display: ["none", "none", "none", "block"], fontWeight: 700, fontSize: 5, color: "#333" }}>
                        Cổng thông tin đăng ký tín chỉ
                    </Text>
                </Box>
            </Box>

            {/* Right: menu + user — cho wrap xuống dòng ở tablet (768–1011) để không tràn ngang */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    flexWrap: "wrap",
                    rowGap: 2,
                    gap: [2, 2, 2, 3],
                    minWidth: 0,
                }}
            >
                {/* Nav menu */}
                <Box
                    as="nav"
                    sx={{
                        display: ["none", "none", "flex"],
                        alignItems: "center",
                        gap: 0,
                    }}
                >
                    {navItems.map(item => {
                        if (item.requireAccess && !hasAccess) return null;
                        const active = item.to && location.pathname === item.to;
                        const sharedSx = {
                            p: "5px 8px",
                            mx: "2px",
                            fontSize: 2,
                            color: "#333",
                            textDecoration: "none",
                            textAlign: "center" as const,
                            display: "block",
                            fontWeight: 700,
                            transition: "0.3s all ease-in-out",
                            border: 0,
                            bg: active ? colors.primarySubtle : "transparent",
                            borderRadius: 3,
                            cursor: "pointer" as const,
                            fontFamily: "inherit",
                            ":hover": { bg: colors.primarySubtle, borderRadius: 3 },
                        };

                        if (item.external) {
                            return (
                                <Box
                                    key={item.label}
                                    as="a"
                                    href={item.href}
                                    target="_blank"
                                    rel="noopener"
                                    sx={sharedSx}
                                >
                                    {item.label}
                                </Box>
                            );
                        }
                        return (
                            <Box
                                key={item.label}
                                as="button"
                                onClick={() => item.to && navigate(item.to)}
                                sx={sharedSx}
                            >
                                {item.label}
                            </Box>
                        );
                    })}
                </Box>

                {/* TC + Thời gian info — clone Old Index.cshtml top-of-section info, chuyển lên header.
                    Hiển thị md+ screen (sm/xs hidden để tránh chật).
                    "Đã ĐK" reactive theo registration.myList → tự update khi register/cancel. */}
                {(hasTcInfo || hasTimeInfo) && (
                    <Box
                        sx={{
                            display: ["none", "none", "flex"],
                            flexDirection: "column",
                            alignItems: "flex-end",
                            lineHeight: 1.3,
                            fontSize: 1,
                            color: "#333",
                            borderRight: "1px solid #ddd",
                            pr: 3,
                        }}
                    >
                        {hasTcInfo && (
                            <Text sx={{ fontWeight: 600 }}>
                                Số TC: <Text sx={{ color: colors.primary }}>{user!.so_tc_min_duoc_dk ?? 0}</Text>
                                {" – "}
                                <Text sx={{ color: colors.primary }}>{user!.so_tc_max_duoc_dk ?? 0}</Text>
                                {tongTC > 0 && (
                                    <>
                                        {" • Đã ĐK: "}
                                        <Text sx={{ color: colors.danger, fontWeight: 700 }}>{tongTC}</Text>
                                    </>
                                )}
                                {/* Số dư + Còn lại — CHỈ render khi đợt bật tinh_hoc_phi_theo_so_du.
                                    Cờ off (đa số đợt thường) → bỏ hẳn, tránh "—" gây nhiễu.
                                    co_so_du=false ⇒ SV không có row ACC_SoDuSinhVien (vd miễn giảm 100%
                                    không cần nộp) → hiển thị 0đ, KHÔNG còn "Chưa có" đỏ. */}
                                {showMoneyOnHeader && (
                                    <>
                                        {" • Số dư: "}
                                        <Text sx={{ color: colors.primary, fontWeight: 700 }}>
                                            {`${(financial?.so_du ?? 0).toLocaleString("vi-VN")}đ`}
                                        </Text>
                                        {" • Còn lại: "}
                                        <Text sx={{
                                            color: vuotSoDuOnHeader ? colors.danger : colors.primary,
                                            fontWeight: 700,
                                        }}>
                                            {`${conLaiOnHeader.toLocaleString("vi-VN")}đ`}
                                        </Text>
                                    </>
                                )}
                            </Text>
                        )}
                        {hasTimeInfo && (
                            <Text sx={{ fontSize: 0, color: colors.danger, fontWeight: 500 }}>
                                <i className="far fa-clock" style={{ marginRight: 4 }} />
                                {formatTime(startTime)}
                                {" → "}
                                {formatTime(endTime)}
                            </Text>
                        )}
                    </Box>
                )}

                {/* Countdown phiên — desktop hiện ở đây; mobile chuyển xuống MobileInfoBar
                    (chỉ mount 1 instance để logic auto-logout không chạy 2 lần). */}
                {user && !isMobile && <SessionTimer />}

                {/* Queue position indicator */}
                {!hasAccess && inQueue && position != null && (
                    <Box
                        sx={{
                            display: ["none", "flex"],
                            alignItems: "center",
                            gap: 1,
                            color: colors.danger,
                            fontSize: 2,
                            fontWeight: 600,
                        }}
                    >
                        <i className="fas fa-hourglass-half" />
                        <Text>Vị trí: {position.toLocaleString("vi-VN")}</Text>
                    </Box>
                )}

                {hasAccess && (
                    <Text
                        sx={{
                            display: ["none", "flex"],
                            alignItems: "center",
                            gap: 1,
                            color: colors.success,
                            fontSize: 2,
                            fontWeight: 600,
                        }}
                    >
                        <i className="fas fa-check-circle" /> Sẵn sàng
                    </Text>
                )}

                {/* User info + dropdown — Old style: avatar 40px circle + ma_sv-ho_ten + lop-khoa */}
                {user && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            pl: 3,
                            borderLeft: "1px solid #ddd",
                            minWidth: 0,
                        }}
                    >
                        <Box
                            sx={{
                                border: "1px solid #ddd",
                                width: 40,
                                height: 40,
                                overflow: "hidden",
                                borderRadius: "50%",
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bg: colors.primarySubtle,
                                color: colors.primary,
                            }}
                        >
                            <i className="fas fa-user" style={{ fontSize: 18 }} />
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                lineHeight: 1.25,
                                minWidth: 0,
                                // Mobile: tên đặt TRƯỚC avatar → avatar trở lại sát nút menu như cũ.
                                order: isMobile ? -1 : 0,
                            }}
                        >
                            {isMobile ? (
                                <>
                                    <Text
                                        sx={{
                                            fontSize: 1,
                                            color: "#333",
                                            fontWeight: 700,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {user.ho_ten}
                                    </Text>
                                    <Text
                                        sx={{
                                            fontSize: 0,
                                            color: "#8a9099",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        <i className="fas fa-id-badge" style={{ marginRight: 3 }} />
                                        {user.ma_sv}
                                        {user.ten_lop && (
                                            <Text
                                                as="span"
                                                sx={{ ml: 2, color: colors.primary, fontWeight: 600 }}
                                            >
                                                <i
                                                    className="fas fa-graduation-cap"
                                                    style={{ marginRight: 3 }}
                                                />
                                                Lớp {user.ten_lop}
                                            </Text>
                                        )}
                                    </Text>
                                </>
                            ) : (
                                <>
                                    <Text sx={{ fontSize: 2, color: "#333", fontWeight: 600 }}>
                                        {user.ma_sv} - {user.ho_ten}
                                    </Text>
                                    <Text sx={{ fontSize: 1, color: "#666", maxWidth: 300 }}>
                                        Lớp: {user.ten_lop} - Khoa: {user.ten_khoa}
                                    </Text>
                                </>
                            )}
                        </Box>

                        {/* 3-dot menu icon */}
                        <ActionMenu>
                            <ActionMenu.Anchor>
                                <Box
                                    as="button"
                                    aria-label="Tài khoản"
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        width: 20,
                                        height: 15,
                                        cursor: "pointer",
                                        bg: "transparent",
                                        border: 0,
                                        padding: 0,
                                        ml: 1,
                                    }}
                                >
                                    <Box
                                        as="span"
                                        sx={{
                                            display: "block",
                                            height: "2px",
                                            width: "100%",
                                            bg: "#333",
                                            borderRadius: "2px",
                                        }}
                                    />
                                    <Box
                                        as="span"
                                        sx={{
                                            display: "block",
                                            height: "2px",
                                            width: "100%",
                                            bg: "#333",
                                            borderRadius: "2px",
                                        }}
                                    />
                                    <Box
                                        as="span"
                                        sx={{
                                            display: "block",
                                            height: "2px",
                                            width: "100%",
                                            bg: "#333",
                                            borderRadius: "2px",
                                        }}
                                    />
                                </Box>
                            </ActionMenu.Anchor>
                            <ActionMenu.Overlay align="end" sx={{ zIndex: 100 }}>
                                <ActionList>
                                    <ActionList.Item onSelect={handleChangePassword}>
                                        Thay đổi mật khẩu
                                    </ActionList.Item>
                                    {devSession && (
                                        <ActionList.Item onSelect={() => setIsDevResetOpen(true)}>
                                            Reset mật khẩu SV (hỗ trợ)
                                        </ActionList.Item>
                                    )}
                                    <ActionList.Divider />
                                    <ActionList.Item variant="danger" onSelect={handleLogout}>
                                        Đăng xuất
                                    </ActionList.Item>
                                </ActionList>
                            </ActionMenu.Overlay>
                        </ActionMenu>
                    </Box>
                )}
            </Box>
          </Box>

            {/* Dải thông tin SV + chỉ số — chỉ bản mobile (header xs vốn trống) */}
            {isMobile && user && <MobileInfoBar user={user} tongTC={tongTC} />}

            <ChangePasswordDialog
                isOpen={isChangePassOpen}
                onClose={() => setIsChangePassOpen(false)}
            />

            {devSession && (
                <DevResetPasswordDialog
                    isOpen={isDevResetOpen}
                    onClose={() => setIsDevResetOpen(false)}
                />
            )}
        </Box>
    );
};

export default Header;
